/**
 * RoleGuard — route-level role check.
 *
 * Wraps children + redirects to dashboard nếu user không thuộc allowedRoles.
 * Khác PermissionGuard (permission-key based) — RoleGuard dùng role string
 * cho các trang admin đơn giản (commission config, payouts, review...).
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAuth, type UserRole } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';

interface RoleGuardProps {
  allowedRoles: UserRole[];
  children: ReactNode;
  /** Show 403 page thay vì redirect (default redirect). */
  showForbidden?: boolean;
}

export function RoleGuard({ allowedRoles, children, showForbidden = true }: RoleGuardProps) {
  const { user, hasRole } = useAuth();

  if (!user) return null;

  if (hasRole(allowedRoles)) {
    return <>{children}</>;
  }

  if (showForbidden) {
    return (
      <Result
        status="403"
        title="Không có quyền"
        subTitle={`Trang này chỉ dành cho ${allowedRoles.join(', ')}. Role hiện tại: ${user.role}.`}
        extra={
          <Button type="primary" onClick={() => window.history.back()}>
            Quay lại
          </Button>
        }
      />
    );
  }

  return <Navigate to={ROUTES.dashboard} replace />;
}
